import { useState, useEffect, useCallback } from 'react';
import { X, Trash2, Sparkles, XCircle, Mail } from 'lucide-react';
import { inventoryApi } from '../../services/inventoryApi';
import { FlagConfirmModal } from './FlagConfirmModal';
import { EmailComposerModal } from '../email/EmailComposerModal';
import type { FlaggedItem } from '../../types/inventory';

interface FlaggedItemsModalProps {
    flagType: 'ToBeDestroyed' | 'NewProduct';
    canEdit?: boolean;
    onClose: () => void;
    onFlagRemoved?: () => void;
}

function formatEUR(value: number): string {
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('de-CH');
}

const FLAG_CONFIG = {
    ToBeDestroyed: {
        title: 'To Be Destroyed',
        icon: Trash2,
        iconColor: 'text-red-600',
        headerBg: 'bg-red-50',
        borderColor: 'border-red-200',
    },
    NewProduct: {
        title: 'New Products',
        icon: Sparkles,
        iconColor: 'text-blue-600',
        headerBg: 'bg-blue-50',
        borderColor: 'border-blue-200',
    },
};

export function FlaggedItemsModal({ flagType, canEdit, onClose, onFlagRemoved }: FlaggedItemsModalProps) {
    const [items, setItems] = useState<FlaggedItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [removingItem, setRemovingItem] = useState<FlaggedItem | null>(null);
    const [showEmailModal, setShowEmailModal] = useState(false);

    const config = FLAG_CONFIG[flagType];
    const Icon = config.icon;

    const loadItems = useCallback(() => {
        setLoading(true);
        inventoryApi.getFlaggedItems(flagType)
            .then(setItems)
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [flagType]);

    useEffect(() => { loadItems(); }, [loadItems]);

    const handleRemoveConfirm = useCallback(async (reason?: string) => {
        if (!removingItem) return;
        const item = removingItem;
        setRemovingItem(null);
        try {
            await inventoryApi.removeFlag(item.ItemID, flagType, reason);
            // Remove from local list immediately
            setItems(prev => prev.filter(i => i.Id !== item.Id));
            onFlagRemoved?.();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to remove flag');
        }
    }, [removingItem, flagType, onFlagRemoved]);

    const totalValue = items.reduce((sum, item) => sum + item.StockValueEUR, 0);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className={`flex items-center justify-between px-6 py-4 border-b ${config.borderColor} ${config.headerBg} rounded-t-lg`}>
                    <div className="flex items-center gap-2">
                        <Icon className={`h-5 w-5 ${config.iconColor}`} />
                        <h2 className="text-lg font-semibold text-gray-900">{config.title}</h2>
                        <span className="ml-2 text-sm text-gray-500">
                            {items.length} items {flagType === 'ToBeDestroyed' && `/ ${formatEUR(totalValue)} EUR`}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {flagType === 'ToBeDestroyed' && canEdit && items.length > 0 && (
                            <button
                                onClick={() => setShowEmailModal(true)}
                                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                title="Send this list as an email"
                            >
                                <Mail className="h-3.5 w-3.5" />
                                Send as Email
                            </button>
                        )}
                        <button onClick={onClose} className="rounded-md p-1 hover:bg-gray-200">
                            <X className="h-5 w-5 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto px-6 py-4">
                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                        </div>
                    )}
                    {error && (
                        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}
                    {!loading && !error && items.length === 0 && (
                        <p className="text-center text-sm text-gray-500 py-8">No flagged items found.</p>
                    )}
                    {!loading && !error && items.length > 0 && (
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Co.</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Product</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Supplier</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Warehouse</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase text-gray-500">Qty</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase text-gray-500">Value EUR</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Flagged</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">By</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Reason</th>
                                    {canEdit && (
                                        <th className="px-3 py-2 text-center text-xs font-medium uppercase text-gray-500">Action</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {items.map(item => (
                                    <tr key={item.Id} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 font-medium text-gray-700">{item.CompanyCode}</td>
                                        <td className="px-3 py-2">
                                            <div className="text-gray-900">{item.ProductName}</div>
                                            <div className="text-xs text-gray-400">{item.ProductCode}</div>
                                        </td>
                                        <td className="px-3 py-2 text-gray-700">{item.SupplierName}</td>
                                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                                            {item.WarehouseName}
                                            {item.WarehouseCountry && <span className="ml-1 text-xs text-gray-400">({item.WarehouseCountry})</span>}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-gray-700">{Math.round(item.StockQuantity).toLocaleString('de-CH')}</td>
                                        <td className="px-3 py-2 text-right font-mono font-medium text-gray-900">{formatEUR(item.StockValueEUR)}</td>
                                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDate(item.CreatedAt)}</td>
                                        <td className="px-3 py-2 text-gray-600 text-xs">{item.CreatedBy}</td>
                                        <td className="px-3 py-2 text-gray-600 text-xs">{item.Reason || '-'}</td>
                                        {canEdit && (
                                            <td className="px-3 py-2 text-center">
                                                <button
                                                    onClick={() => setRemovingItem(item)}
                                                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                                                    title="Remove flag"
                                                >
                                                    <XCircle className="h-3.5 w-3.5" />
                                                    Unflag
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Confirm removal modal */}
            {removingItem && (
                <FlagConfirmModal
                    itemName={removingItem.ProductName}
                    flagType={flagType}
                    action="remove"
                    onConfirm={handleRemoveConfirm}
                    onCancel={() => setRemovingItem(null)}
                />
            )}

            {/* Email Composer (destroy list) */}
            {showEmailModal && (
                <EmailComposerModal
                    isOpen={true}
                    context={{ type: 'DestroyList', items }}
                    onClose={() => setShowEmailModal(false)}
                />
            )}
        </div>
    );
}
